/**
 * Service for handling product images
 */
import { PLACEHOLDER_IMAGE_PATH } from './imageConstants'

export const imageService = {
  /**
   * Gets the full path of the product image based on the foxpro_code
   * @param {string} imagePath - Image path returned by the backend
   * @param {Object} product - Product object (optional)
   * @returns {string} - Full path of the image
   */
  getProductImageUrl(imagePath, product = null) {
    if (!imagePath) {
      console.log('[ImageService] Usando imagem padrão para caminho vazio');
      return PLACEHOLDER_IMAGE_PATH; // Default image if there is no path
    }

    // If the path is already complete (starts with http or https), returns the original path
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      console.log(`[ImageService] Usando URL completa: ${imagePath}`);
      return imagePath;
    }

    // If the path already starts with /, returns the path as is
    // This is important for the images returned by the /products/images/:foxproCode endpoint
    if (imagePath.startsWith('/')) {
      console.log(`[ImageService] Usando caminho relativo: ${imagePath}`);
      return imagePath;
    }

    // If the product and the foxpro_code are available, uses the path with the foxpro_code
    if (product && product.foxpro_code) {
      const fullPath = `/images/products/${product.foxpro_code}/${imagePath}`;
      console.log(`[ImageService] Construindo caminho com foxpro_code: ${fullPath}`);
      return fullPath;
    }

    // Otherwise, adds the /images/products/ prefix
    const fullPath = `/images/products/${imagePath}`;
    console.log(`[ImageService] Construindo caminho sem foxpro_code: ${fullPath}`);
    return fullPath;
  },

  /**
   * Gets all images of a product based on the foxpro_code
   * @param {Object} product - Product object
   * @returns {Array} - Array with the image paths
   */
  getProductImages(product) {
    if (!product) {
      console.log('[ImageService] Produto inválido, retornando array vazio');
      return [];
    }

    console.log('[ImageService] Processando imagens para produto:', product.id, product.name);
    console.log('[ImageService] Dados do produto:', {
      id: product.id,
      name: product.name,
      image: product.image,
      foxpro_code: product.foxpro_code,
      images: product.images
    });

    const mainImage = this.getProductImageUrl(product.image, product);
    console.log(`[ImageService] Imagem principal: ${mainImage}`);

    // If the product has no additional images, returns only the main image
    if (!product.images || !Array.isArray(product.images) || product.images.length === 0) {
      console.log('[ImageService] Sem imagens adicionais, retornando apenas a principal');
      return [mainImage];
    }

    // Processes all additional images
    console.log(`[ImageService] Processando ${product.images.length} imagens adicionais`);
    const additionalImages = product.images.map(img => {
      const processedImg = this.getProductImageUrl(img, product);
      console.log(`[ImageService] Imagem adicional processada: ${processedImg}`);
      return processedImg;
    });

    // Returns an array with the main image and the additional images, removing duplicates
    const allImages = [...new Set([mainImage, ...additionalImages])];
    console.log(`[ImageService] Total de imagens únicas: ${allImages.length}`);
    console.log('[ImageService] Todas as imagens:', allImages);
    return allImages;
  },

  /**
   * Gets the path of the image folder of a product based on the foxpro_code
   * @param {string} foxproCode - FoxPro code of the product
   * @returns {string} - Path of the image folder
   */
  getProductImagesFolder(foxproCode) {
    if (!foxproCode) {
      console.log('[ImageService] Sem código FoxPro, retornando pasta base de produtos');
      return '/images/products';
    }

    const folderPath = `/images/products/${foxproCode}`;
    console.log(`[ImageService] Pasta de imagens para código FoxPro ${foxproCode}: ${folderPath}`);
    return folderPath;
  },

  /**
   * Utility function to handle image loading errors
   * @param {Event} event - Image loading error event
   * @returns {void}
   */
  handleImageError(event) {
    // Tries to use the absolute path for the fallback image
    const baseUrl = window.location.origin;
    const fallbackUrl = `${baseUrl}${PLACEHOLDER_IMAGE_PATH}`;

    // Checks whether the image that failed is already the fallback image
    const isAlreadyFallback = (
      event.target.src === fallbackUrl ||
      event.target.src.includes(PLACEHOLDER_IMAGE_PATH)
    );

    if (isAlreadyFallback) {
      console.error('Fallback image not found:', event.target.src);
      // Minimal transparent base64 image as a last resort
      event.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      event.target.onerror = null; // Prevents infinite loop
      return;
    }

    // Tries to use the fallback image
    console.log(`[ImageService] Usando imagem de fallback: ${fallbackUrl}`);
    event.target.src = fallbackUrl;

    // If even the fallback image fails, use a minimal base64 image
    event.target.onerror = () => {
      console.error('Imagem de fallback falhou');
      // Minimal transparent base64 image as a last resort
      event.target.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
      event.target.onerror = null; // Prevents infinite loop
    };
  }
}
